import { useCallback, useEffect, useState } from 'react'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { useResgatarPareamentoMutation } from '../../../api'
import { montarUrlIdentificador } from '../../../lib/url-identificador'
import { urlTotemPareado } from '../../../lib/url-pdv'
import NumericKeypad from '../checkout/components/numeric-keypad'
import StatusFeedback, { type FeedbackStatus } from '../checkout/components/status-feedback'
import { SobreAparelho } from './sobre-aparelho'

const TAMANHO_CODIGO = 8
const DWELL_SUCESSO_MS = 2500

interface Destino {
  slug: string
  tipo: string
  numero: string | null
  nomeLoja: string
  codigoTroca: string
}

function formatar(codigo: string): string {
  if (codigo.length <= 4) return codigo
  return `${codigo.slice(0, 4)} ${codigo.slice(4)}`
}

export function TotemPareamentoPage(): JSX.Element {
  const theme = useTheme()
  const [params] = useSearchParams()
  const [codigo, setCodigo] = useState('')
  const [status, setStatus] = useState<FeedbackStatus | null>(null)
  const [destino, setDestino] = useState<Destino | null>(null)
  const [erro, setErro] = useState<{ titulo: string; detalhe: string } | null>(null)
  const [resgatar] = useResgatarPareamentoMutation()

  const completo = codigo.length === TAMANHO_CODIGO

  const digitar = useCallback((digito: string) => {
    setCodigo((atual) => (atual.length >= TAMANHO_CODIGO ? atual : atual + digito))
  }, [])

  const apagar = useCallback(() => setCodigo((atual) => atual.slice(0, -1)), [])

  const confirmar = useCallback(async () => {
    if (codigo.length !== TAMANHO_CODIGO) return

    setStatus('loading')
    setErro(null)

    try {
      const resultado = await resgatar({ codigo }).unwrap()
      setDestino(resultado)
      setStatus('sucesso')
    } catch (e) {
      const status429 = (e as { status?: number })?.status === 429
      const semResposta = (e as { status?: unknown })?.status === 'FETCH_ERROR'

      setErro(
        status429
          ? { titulo: 'Muitas tentativas', detalhe: 'Aguarde alguns minutos e tente de novo.' }
          : semResposta
            ? { titulo: 'Sem conexão', detalhe: 'Verifique a internet do aparelho e tente de novo.' }
            : { titulo: 'Código inválido ou expirado', detalhe: 'Peça um código novo no computador.' },
      )
      setStatus('x')
    }
  }, [codigo, resgatar])

  const dispensar = useCallback(() => {
    setStatus(null)
    setErro(null)
    setCodigo('')
  }, [])

  useEffect(() => {
    const daUrl = params.get('codigo')
    if (daUrl && /^\d{8}$/.test(daUrl)) setCodigo(daUrl)
  }, [params])

  useEffect(() => {
    if (status !== 'sucesso' || !destino) return

    const timer = setTimeout(() => {
      const urlPdv = urlTotemPareado(destino.codigoTroca)
      window.location.href = urlPdv ?? montarUrlIdentificador({ tipo: destino.tipo as never, codigo: destino.slug })
    }, DWELL_SUCESSO_MS)

    return () => clearTimeout(timer)
  }, [status, destino])

  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent): void => {
      if (status) {
        if (status === 'x' && evento.key === 'Enter') dispensar()
        return
      }
      if (evento.key === 'Enter') {
        void confirmar()
        return
      }
      if (evento.key === 'Backspace') {
        apagar()
        return
      }
      if (/^[0-9]$/.test(evento.key)) digitar(evento.key)
    }

    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [status, confirmar, apagar, digitar, dispensar])

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      <Stack spacing={4} sx={{ width: '100%', maxWidth: 460, alignItems: 'center' }}>
        <Stack spacing={1} sx={{ alignItems: 'center', textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Parear este aparelho
          </Typography>

          <Typography variant="body1" color="text.secondary">
            No computador, abra <strong>Sischef Cardápio → Acesso ao Cardápio</strong> e clique em{' '}
            <strong>Parear</strong> no ponto de acesso deste totem.
          </Typography>
        </Stack>

        <Box
          sx={{
            width: '100%',
            py: 2.5,
            borderRadius: '12px',
            textAlign: 'center',
            fontSize: '3rem',
            fontWeight: 700,
            letterSpacing: '3px',
            fontFamily: 'monospace',
            minHeight: '5rem',
            color: codigo ? 'text.primary' : 'text.disabled',
            backgroundColor: theme.palette.personal?.header || '#333',
          }}
        >
          {codigo ? formatar(codigo) : '––––––––'}
        </Box>

        <NumericKeypad
          onDigito={digitar}
          onApagar={apagar}
          onConfirmar={() => void confirmar()}
          apagarDesabilitado={codigo.length === 0}
          confirmarDesabilitado={!completo}
        />

        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
          Este é o <strong>código de pareamento</strong>, usado uma vez só. Não é o PIN de configuração.
        </Typography>
      </Stack>

      <SobreAparelho />

      <StatusFeedback
        status={status}
        titulo={
          status === 'loading'
            ? 'Verificando código...'
            : status === 'sucesso'
              ? destino?.nomeLoja ?? 'Aparelho pareado'
              : erro?.titulo ?? 'Não foi possível parear'
        }
        detalhe={
          status === 'sucesso'
            ? destino?.numero?.trim() || 'Abrindo o totem...'
            : status === 'x'
              ? erro?.detalhe
              : undefined
        }
        textoBotao={status === 'x' ? 'Tentar de novo' : undefined}
        onDismiss={status === 'x' ? dispensar : undefined}
        autoRetornoMs={status === 'x' ? 8000 : null}
      />
    </Box>
  )
}
